import * as THREE from 'three';

import vertex from './shaders/vertex.glsl';
import fragment from './shaders/fragment.glsl';

export default class Sketch {
	constructor(options) {
		this.wrap = options.dom;
		this.images = options.images || [];
		this.currentIndex = 0;
		this.nextIndex = this.images.length > 1 ? 1 : 0;
		this.transitionDuration = options.transitionDuration || 2000;
		this.cycleDelay = options.cycleDelay || 6000;
		this.displacementIntensity = options.displacementIntensity || 0.67;
		this.isTransitioning = false;
		this.transitionStart = 0;
		this.transitionTimer = null;
		this.animationFrame = null;

		if (!this.wrap || !this.images.length) {
			return;
		}

		this.clock = new THREE.Clock();
		this.textureLoader = new THREE.TextureLoader();
		this.setSize();

		this.scene = new THREE.Scene();
		this.camera = new THREE.OrthographicCamera(
			this.width / -2,
			this.width / 2,
			this.height / 2,
			this.height / -2,
			1,
			1000
		);
		this.camera.position.z = 1;

		this.renderer = new THREE.WebGLRenderer({
			alpha: true,
			antialias: true
		});
		this.renderer.outputEncoding = THREE.sRGBEncoding;
		this.renderer.setSize(this.width, this.height);
		this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
		this.wrap.appendChild(this.renderer.domElement);

		this.setResize();
		this.init();
	}

	async init() {
		this.textures = await Promise.all(this.images.map((src) => this.loadTexture(src)));
		this.displacementTexture = this.createDisplacementTexture();
		this.setGeometry();
		this.scheduleNextTransition();
		this.render();
	}

	setGeometry() {
		this.geometry = new THREE.PlaneGeometry(this.width, this.height, 1, 1);

		this.mesh = new THREE.Mesh(this.geometry, this.getMaterial());
		this.scene.add(this.mesh);
	}

	getMaterial() {
		const currentTexture = this.textures[this.currentIndex];
		const nextTexture = this.textures[this.nextIndex];
		return new THREE.ShaderMaterial({
			side: THREE.DoubleSide,
			wireframe: false,
			transparent: true,
			fragmentShader: fragment,
			vertexShader: vertex,
			uniforms: {
				transitionProgress: { value: 0 },
				displacementIntensity: { value: this.displacementIntensity },
				texture1: { value: currentTexture },
				texture2: { value: nextTexture },
				displacementTexture: { value: this.displacementTexture },
				resolution: { value: new THREE.Vector2(this.width, this.height) },
				imageResolution1: { value: this.getTextureSize(currentTexture) },
				imageResolution2: { value: this.getTextureSize(nextTexture) },
			}
		});
	}

	loadTexture(src) {
		return new Promise((resolve, reject) => {
			this.textureLoader.load(
				src,
				(texture) => {
					texture.encoding = THREE.sRGBEncoding;
					texture.minFilter = THREE.LinearFilter;
					texture.magFilter = THREE.LinearFilter;
					texture.generateMipmaps = false;
					resolve(texture);
				},
				undefined,
				reject
			);
		});
	}

	createDisplacementTexture() {
		const size = 256;
		const canvas = document.createElement('canvas');
		const context = canvas.getContext('2d');

		canvas.width = size;
		canvas.height = size;

		const gradient = context.createLinearGradient(0, 0, size, size);
		gradient.addColorStop(0, '#111111');
		gradient.addColorStop(0.5, '#8f8f8f');
		gradient.addColorStop(1, '#1f1f1f');
		context.fillStyle = gradient;
		context.fillRect(0, 0, size, size);

		for (let i = 0; i < 80; i += 1) {
			const radius = 10 + Math.random() * 36;
			const x = Math.random() * size;
			const y = Math.random() * size;
			const alpha = 0.08 + Math.random() * 0.18;
			const glow = context.createRadialGradient(x, y, radius * 0.1, x, y, radius);
			glow.addColorStop(0, `rgba(255,255,255,${alpha})`);
			glow.addColorStop(1, 'rgba(0,0,0,0)');
			context.fillStyle = glow;
			context.beginPath();
			context.arc(x, y, radius, 0, Math.PI * 2);
			context.fill();
		}

		const texture = new THREE.CanvasTexture(canvas);
		texture.wrapS = THREE.RepeatWrapping;
		texture.wrapT = THREE.RepeatWrapping;
		texture.minFilter = THREE.LinearFilter;
		texture.magFilter = THREE.LinearFilter;
		return texture;
	}

	getTextureSize(texture) {
		const image = texture?.image;
		return new THREE.Vector2(image?.naturalWidth || image?.width || 1, image?.naturalHeight || image?.height || 1);
	}

	scheduleNextTransition() {
		if (this.images.length < 2) {
			return;
		}

		clearTimeout(this.transitionTimer);
		this.transitionTimer = window.setTimeout(() => {
			this.startTransition();
		}, this.cycleDelay);
	}

	startTransition() {
		if (!this.mesh || this.isTransitioning || this.images.length < 2) {
			return;
		}

		this.isTransitioning = true;
		this.transitionStart = performance.now();
		this.nextIndex = (this.currentIndex + 1) % this.textures.length;
		this.mesh.material.uniforms.texture2.value = this.textures[this.nextIndex];
		this.mesh.material.uniforms.imageResolution2.value = this.getTextureSize(this.textures[this.nextIndex]);
	}

	easeExpoInOut(progress) {
		if (progress <= 0) {
			return 0;
		}

		if (progress >= 1) {
			return 1;
		}

		if (progress < 0.5) {
			return Math.pow(2, 20 * progress - 10) / 2;
		}

		return (2 - Math.pow(2, -20 * progress + 10)) / 2;
	}

	updateMaterial(now) {
		if (!this.mesh) {
			return;
		}

		const uniforms = this.mesh.material.uniforms;

		if (!this.isTransitioning) {
			uniforms.transitionProgress.value = 0;
			return;
		}

		const elapsed = now - this.transitionStart;
		const progress = Math.min(elapsed / this.transitionDuration, 1);
		uniforms.transitionProgress.value = this.easeExpoInOut(progress);

		if (progress >= 1) {
			this.isTransitioning = false;
			this.currentIndex = this.nextIndex;
			uniforms.transitionProgress.value = 0;
			uniforms.texture1.value = this.textures[this.currentIndex];
			uniforms.imageResolution1.value = this.getTextureSize(this.textures[this.currentIndex]);
			this.scheduleNextTransition();
		}
	}

	render(now = performance.now()) {
		this.updateMaterial(now);
		this.renderer.render(this.scene, this.camera);
		this.animationFrame = requestAnimationFrame(this.render.bind(this));
	}

	setResize() {
		window.addEventListener('resize', this.onResize.bind(this));
	}

	setSize() {
		this.width = this.wrap.offsetWidth;
		this.height = this.wrap.offsetHeight;
	}

	onResize() {
		if (!this.wrap || !this.camera || !this.renderer) {
			return;
		}

		this.setSize();
		this.camera.left = this.width / -2;
		this.camera.right = this.width / 2;
		this.camera.top = this.height / 2;
		this.camera.bottom = this.height / -2;
		this.camera.updateProjectionMatrix();

		this.renderer.setSize(this.width, this.height);
		this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

		if (this.mesh) {
			this.mesh.geometry.dispose();
			this.mesh.geometry = new THREE.PlaneGeometry(this.width, this.height, 1, 1);
			this.mesh.material.uniforms.resolution.value.set(this.width, this.height);
		}
	}
}


window.addEventListener('load', () => {
	const $stage = document.querySelector('#ryder-stage') || document.querySelector('#ryderStage');

	if ($stage) {
		const images = ($stage.dataset.images || '')
			.split(',')
			.map((src) => src.trim())
			.filter(Boolean);

		new Sketch({
			dom: $stage,
			images,
		});
	}
});
